<template>
  <div class="assessment-page">
    <div v-if="loading" class="state-card">
      <v-progress-circular indeterminate color="#6B35C8"></v-progress-circular>
    </div>

    <div v-else-if="!courses.length" class="state-card">
      <v-icon color="#6B35C8" large>mdi-clipboard-text-search-outline</v-icon>
      <div class="state-title">هنوز دوره ای برای ارزیابی ندارید.</div>
    </div>

    <div v-else class="assessment-wrap">
      <div class="hero-card">
        <div class="hero-top">
          <div class="avatar">{{ student.avatar_initials || initials(student.name) }}</div>
          <div class="hero-name">{{ student.name }}</div>
        </div>

        <div class="course-tabs">
          <button
              v-for="course in courses"
              :key="course.class_id"
              type="button"
              class="course-tab"
              :class="{ active: course.class_id === selectedClassId }"
              @click="selectCourse(course)"
          >
            <span class="status-dot" :class="course.pace.key"></span>
            {{ course.title }}
          </button>
        </div>

        <div class="hero-chips" v-if="selectedCourse">
          <div class="chip">
            <div class="chip-key">دوره</div>
            <div class="chip-value">🎓 {{ selectedCourse.course_type || 'دوره آموزشی' }}</div>
          </div>
          <div class="chip">
            <div class="chip-key">مربی</div>
            <div class="chip-value">{{ selectedCourse.instructor || 'ثبت نشده' }}</div>
          </div>
          <div class="chip">
            <div class="chip-key">شروع</div>
            <div class="chip-value">{{ selectedCourse.start_date || 'ثبت نشده' }}</div>
          </div>
          <div class="chip">
            <div class="chip-key">امروز</div>
            <div class="chip-value">روز {{ faNumber(selectedCourse.day_of_course) }} ام</div>
          </div>
        </div>
      </div>

      <div class="section-tag">
        <h2>خلاصه تلاش</h2>
      </div>

      <div class="white-card" v-if="selectedCourse">
        <div class="week-summary">
          <div class="week-head">
            <div>
              <div class="week-title">این هفته</div>
              <div class="week-sub">{{ faNumber(weeklySummary.days_to_end) }} روز تا پایان هفته</div>
            </div>
            <span class="week-status" :class="weeklySummary.status.key">
              <span class="week-status-dot"></span>
              {{ weeklySummary.status.label }}
            </span>
          </div>

          <div class="week-dots">
            <div v-for="day in weeklySummary.days" :key="day.date" class="week-dot-col">
              <span class="week-letter">{{ day.letter }}</span>
              <span
                  class="week-dot"
                  :class="{ done: day.done, future: day.is_future, today: day.is_today }"
              ></span>
            </div>
          </div>

          <div class="week-stats">
            <span><b>{{ faNumber(weeklySummary.practice_days) }}</b> روز تمرین</span>
            <span class="sep"></span>
            <span><b>{{ faNumber(weeklySummary.total_score) }}</b> امتیاز</span>
          </div>
        </div>

        <div class="lessons-summary">
          <div class="lessons-row">
            <span class="lessons-key">درس‌ها</span>
            <span class="lessons-value">
              {{ faNumber(selectedCourse.opened_lessons) }}
              <small> از {{ faNumber(selectedCourse.total_lessons) }}</small>
            </span>
          </div>
          <div class="lessons-bar">
            <i :style="{ width: lessonProgressPercent + '%' }"></i>
          </div>
        </div>

        <div class="summary-tiles">
          <div class="summary-tile">
            <div class="tile-number">{{ faNumber(calendar.streak_days) }}</div>
            <div class="tile-title">🔥 روز زنجیره</div>
          </div>
          <div class="summary-tile">
            <div class="tile-number">{{ faNumber(calendar.practice_days_count) }}</div>
            <div class="tile-title">📅 روز تمرین ماه</div>
          </div>
          <div class="summary-tile">
            <div class="tile-number">{{ faNumber(calendar.total_score) }}</div>
            <div class="tile-title">⭐ امتیاز ماه</div>
          </div>
        </div>
        <v-menu v-if="socialEnabled && Number(calendar.total_score) > 0" offset-y>
          <template v-slot:activator="{ on, attrs }"><v-btn small text color="#6B35C8" v-bind="attrs" v-on="on"><v-icon left small>mdi-share-variant</v-icon>اشتراک دستاورد ماه</v-btn></template>
          <v-list dense><v-list-item @click="shareAchievement('class')"><v-list-item-title>هم‌کلاسی‌ها</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('course')"><v-list-item-title>هم‌دوره‌ای‌ها</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('all')"><v-list-item-title>همه هنرجوهای درک</v-list-item-title></v-list-item><v-list-item @click="shareAchievement('public')"><v-list-item-title>عمومی با لینک</v-list-item-title></v-list-item></v-list>
        </v-menu>
      </div>

      <div class="white-card calendar-card">
        <div class="calendar-head">
          <div class="calendar-title">
            <div class="calendar-heading">تقویم تلاش روزانه</div>
            <div class="calendar-subtitle">{{ calendar.month_title }}</div>
          </div>
          <div class="calendar-nav">
            <button type="button" :disabled="calendarLoading || !calendar.previous_month" @click="changeMonth(calendar.previous_month)" title="ماه قبل">‹</button>
            <button type="button" :disabled="calendarLoading || !calendar.next_month" @click="changeMonth(calendar.next_month)" title="ماه بعد">›</button>
          </div>
        </div>

        <div class="calendar-grid" :class="{ loading: calendarLoading }">
          <div v-for="weekday in weekdays" :key="weekday" class="calendar-weekday">{{ weekday }}</div>
          <div v-for="index in calendar.start_offset" :key="'empty-' + index" class="calendar-day calendar-empty"></div>
          <div
              v-for="day in calendar.days"
              :key="day.day"
              class="calendar-day"
              :class="['p' + day.level, { today: day.is_today, future: isFutureDay(day) }]"
              :style="dayStyle(day)"
              :title="dayTitle(day)"
          >
            <span class="calendar-date">{{ faNumber(day.day) }}</span>
            <span v-if="day.score > 0" class="calendar-score">{{ faNumber(day.score) }}</span>
          </div>
        </div>

        <div class="calendar-legend">
          <span
              v-for="level in calendar.levels"
              :key="level.index"
              :style="{ backgroundColor: level.color }"
              :title="level.label"
          ></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AssessmentsMobile",
  data() {
    return {
      loading: true,
      calendarLoading: false,
      student: {},
      courses: [],
      selectedClassId: null,
      selectedMonth: null,
      socialEnabled: false,
      weekdays: ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']
    }
  },
  computed: {
    selectedCourse() {
      return this.courses.find(course => course.class_id === this.selectedClassId) || this.courses[0]
    },
    calendar() {
      return this.selectedCourse?.calendar || this.defaultCalendar()
    },
    weeklySummary() {
      return this.selectedCourse?.weekly_summary || this.defaultWeeklySummary()
    },
    lessonProgressPercent() {
      const percent = Math.max(0, Math.min(100, Number(this.selectedCourse?.progress_percent || 0)))
      return percent
    }
  },
  mounted() {
    this.getAssessment(null, true)
  },
  watch: { selectedClassId() { this.checkSocialAvailability() } },
  methods: {
    checkSocialAvailability() {
      if (!this.selectedCourse) { this.socialEnabled = false; return }
      window.axios.get('student/social/status', { params: { class_id: this.selectedCourse.class_id, course_id: this.selectedCourse.course_id } }).then(res => { this.socialEnabled = !!res.data.enabled }).catch(() => { this.socialEnabled = false })
    },
    shareAchievement(audience) {
      if (!this.selectedCourse) return
      window.axios.post('student/social/posts', {
        post_type: 'achievement', audience, online_class_id: this.selectedCourse.class_id,
        course_id: this.selectedCourse.course_id,
        content: `دستاورد من در ${this.selectedCourse.title}: ${this.calendar.total_score} امتیاز در ${this.calendar.month_title} ⭐`
      }).then(res => this.openSnackbar(res.data.status === 'pending' ? 'دستاورد برای تأیید ارسال شد.' : 'دستاوردت در شبکه اجتماعی منتشر شد.', '#6B35C8'))
          .catch(err => this.openSnackbar(err.response?.data?.message || 'اشتراک‌گذاری انجام نشد.', '#F1416C'))
    },
    getAssessment(month = this.selectedMonth, initialLoad = false) {
      if (initialLoad) {
        this.loading = true
      } else {
        this.calendarLoading = true
      }

      window.axios.get('student/assessment/details', {
        params: month ? {month} : {}
      }).then((res) => {
        const data = res.data.data || {}
        const previousClassId = this.selectedClassId
        this.student = data.student || {}
        this.courses = data.courses || []
        this.selectedClassId = this.courses.some(course => course.class_id === previousClassId)
            ? previousClassId
            : this.courses[0]?.class_id || null
        this.selectedMonth = this.calendar.month || month || null
        this.loading = false
      }).catch((err) => {
        this.loading = false
        if (err.response?.status === 422) {
          this.showValidationErrors(err)
        } else {
          this.openSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات ارزیابی', '#F1416C')
        }
      }).finally(() => {
        this.calendarLoading = false
      })
    },
    defaultCalendar() {
      return {
        month_title: '',
        start_offset: 0,
        practice_days_count: 0,
        streak_days: 0,
        total_score: 0,
        previous_month: null,
        next_month: null,
        score_delta_percent: 0,
        levels: [],
        days: []
      }
    },
    defaultWeeklySummary() {
      return {
        target: 4,
        days_to_end: 0,
        practice_days: 0,
        total_score: 0,
        status: {key: 'good', label: 'خوب'},
        days: ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((letter, index) => ({
          date: String(index),
          letter,
          score: null,
          is_future: true,
          is_today: false,
          done: false
        }))
      }
    },
    selectCourse(course) {
      this.selectedClassId = course.class_id
    },
    changeMonth(month) {
      if (!month || this.calendarLoading) {
        return
      }

      this.selectedMonth = month
      this.getAssessment(month)
    },
    dayStyle(day) {
      if (!day || !day.color || this.isFutureDay(day)) {
        return {}
      }

      return {
        backgroundColor: day.color,
        color: this.readableTextColor(day.color)
      }
    },
    isFutureDay(day) {
      return Boolean(this.calendar.today && day && Number(day.day) > Number(this.calendar.today))
    },
    dayTitle(day) {
      return `${this.faNumber(day.score || 0)} امتیاز${day.label ? ' - ' + day.label : ''}`
    },
    readableTextColor(hex) {
      const value = String(hex || '#ffffff').replace('#', '')
      const r = parseInt(value.substring(0, 2), 16)
      const g = parseInt(value.substring(2, 4), 16)
      const b = parseInt(value.substring(4, 6), 16)
      return ((r * 299 + g * 587 + b * 114) / 1000) > 150 ? '#1A0A3C' : '#FFFFFF'
    },
    faNumber(value) {
      return String(value ?? 0).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    initials(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) {
        return 'هـ'
      }
      return parts.length > 1 ? `${parts[0][0]}‌${parts[1][0]}` : parts[0][0]
    }
  }
}
</script>

<style scoped>
.assessment-page {
  min-height: 100vh;
  background: #EFEAF9;
  padding: 18px 14px 70px;
  direction: rtl;
}

.assessment-wrap {
  max-width: 430px;
  margin: 0 auto;
}

.state-card {
  min-height: 220px;
  background: #fff;
  border: 1px solid #F1EBFC;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(107, 53, 200, .08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 14px;
}

.state-title {
  font-size: 13px;
  font-weight: 700;
  color: #1A0A3C;
}

.hero-card {
  background: linear-gradient(135deg, #6B35C8 0%, #8B4FE0 60%, #A06BF0 100%);
  border-radius: 24px;
  padding: 20px 20px 18px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(107, 53, 200, .35);
}

.hero-card::before {
  content: "";
  position: absolute;
  left: -40px;
  top: -40px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .08);
}

.hero-card::after {
  content: "";
  position: absolute;
  left: 30px;
  bottom: -60px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(245, 146, 10, .18);
}

.hero-top,
.course-tabs,
.hero-chips {
  position: relative;
  z-index: 1;
}

.hero-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  color: #6B35C8;
  border: 3px solid rgba(255, 255, 255, .5);
  flex-shrink: 0;
}

.hero-name {
  font-size: 18px;
  font-weight: 800;
}

.course-tabs {
  display: flex;
  gap: 7px;
  margin-top: 16px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.course-tab {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 99px;
  background: rgba(255, 255, 255, .14);
  color: rgba(255, 255, 255, .8);
  border: 1px solid rgba(255, 255, 255, .18);
  white-space: nowrap;
}

.course-tab.active {
  background: #fff;
  color: #6B35C8;
  border-color: #fff;
}

.status-dot,
.pace-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: currentColor;
}

.status-dot.excellent {
  background: #22C55E;
}

.status-dot.ok {
  background: #F5920A;
}

.status-dot.needs_practice {
  background: #EF4444;
}

.hero-chips {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 14px;
}

.chip {
  background: rgba(255, 255, 255, .14);
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 14px;
  padding: 9px 12px;
}

.chip-key {
  font-size: 10px;
  opacity: .72;
}

.chip-value {
  font-size: 12.5px;
  font-weight: 700;
  margin-top: 2px;
  min-height: 20px;
}

.section-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 12px;
}

.section-tag.compact {
  margin-top: 22px;
}

.section-tag .num {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #6B35C8;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-tag h2 {
  font-size: 14px;
  font-weight: 800;
  color: #1A0A3C;
  margin: 0;
}

.white-card {
  background: #fff;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(107, 53, 200, .08);
  border: 1px solid #F1EBFC;
}

.week-summary {
  background: #F7F3FD;
  border-radius: 16px;
  padding: 14px 14px 12px;
}

.week-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.week-title {
  font-size: 12.5px;
  font-weight: 800;
  color: #1A0A3C;
  line-height: 1.5;
}

.week-sub {
  font-size: 10.5px;
  color: #8C8C9A;
  font-weight: 600;
}

.week-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 99px;
  white-space: nowrap;
}

.week-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.week-status.great {
  background: #DCF5E6;
  color: #14713F;
}

.week-status.good {
  background: #F0E8FF;
  color: #6B35C8;
}

.week-status.low {
  background: #FFF1E0;
  color: #B45309;
}

.week-dots {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  justify-items: center;
}

.week-dot-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.week-letter {
  font-size: 10px;
  font-weight: 600;
  color: #B5B5C0;
  line-height: 1;
}

.week-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #E9E8F0;
}

.week-dot.done {
  background: #5ACC87;
}

.week-dot.future {
  background: transparent;
  border: 1.5px dashed #DCDCE4;
}

.week-dot.today {
  box-shadow: inset 0 0 0 1.5px #F5920A;
}

.week-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 12px;
  color: #8C8C9A;
  font-weight: 600;
}

.week-stats b {
  font-size: 14px;
  font-weight: 800;
  color: #1A0A3C;
}

.lessons-summary {
  margin-top: 16px;
}

.lessons-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.lessons-key {
  font-size: 11.5px;
  color: #8C8C9A;
  font-weight: 600;
}

.lessons-value {
  font-size: 14px;
  font-weight: 800;
  color: #1A0A3C;
}

.lessons-value small {
  font-size: 11.5px;
  font-weight: 600;
  color: #B5B5C0;
}

.lessons-bar {
  height: 6px;
  border-radius: 99px;
  background: #F0E8FF;
  margin-top: 8px;
  overflow: hidden;
}

.lessons-bar i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: #6B35C8;
}

.summary-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
}

.summary-tile {
  background: #F7F7FA;
  border-radius: 14px;
  padding: 10px 8px;
  text-align: center;
  min-width: 0;
}

.tile-number {
  font-size: 17px;
  font-weight: 800;
  color: #1A0A3C;
  line-height: 1.3;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 5px;
}

.tile-title {
  font-size: 10px;
  color: #8C8C9A;
  font-weight: 600;
  margin-top: 3px;
  line-height: 1.6;
}

.progress-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 26px;
}

.ring {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.ring svg {
  transform: rotate(-90deg);
}

.ring-value {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ring-value b {
  font-size: 21px;
  color: #6B35C8;
}

.ring-value span {
  font-size: 9.5px;
  color: #9E9E9E;
}

.progress-info {
  text-align: right;
}

.lessons-label {
  font-size: 11.5px;
  color: #9E9E9E;
  font-weight: 600;
}

.lessons-num {
  font-size: 19px;
  font-weight: 800;
  color: #1A1A1A;
  margin-top: 2px;
}

.lessons-num small {
  font-size: 12px;
  font-weight: 600;
  color: #9E9E9E;
}

.pace {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 700;
  padding: 7px 13px;
  border-radius: 99px;
}

.pace.excellent {
  background: #DCFCE7;
  color: #14803C;
}

.pace.ok {
  background: #F0E8FF;
  color: #6B35C8;
}

.pace.needs_practice {
  background: #FEE2E2;
  color: #B91C1C;
}

.calendar-card {
  margin-top: 16px;
  padding: 18px 14px;
  min-width: 0;
}

.calendar-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.calendar-card .calendar-head {
  padding: 0 4px;
}

.calendar-heading {
  font-size: 14px;
  font-weight: 800;
  color: #1A0A3C;
  line-height: 1.5;
}

.calendar-subtitle {
  font-size: 11px;
  color: #8C8C9A;
  font-weight: 600;
}

.calendar-nav {
  display: flex;
  gap: 6px;
}

.calendar-nav button {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  border: 1px solid #EEEEEE;
  background: #fff;
  cursor: pointer;
  color: #616171;
  font-size: 13px;
  font-family: inherit;
}

.calendar-nav button:disabled {
  opacity: .35;
  cursor: default;
}

.sep {
  width: 1px;
  height: 14px;
  background: #E0DCEE;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  align-items: start;
  gap: 6px;
  transition: opacity .2s ease;
}

.calendar-grid.loading {
  opacity: .55;
  pointer-events: none;
}

.calendar-weekday {
  text-align: center;
  font-size: 10px;
  color: #B5B5C0;
  font-weight: 600;
  padding-bottom: 4px;
}

.calendar-day {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  aspect-ratio: 4 / 5;
  border-radius: 12px;
  position: relative;
  background: #E9E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0F5A30;
}

.calendar-day .calendar-date {
  position: absolute;
  top: 5px;
  right: 7px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: #7C7C8C;
}

.calendar-day .calendar-score {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  color: #0F5A30;
}

.calendar-day.p1 {
  background: #D3F2DE;
}

.calendar-day.p2 {
  background: #9DE3B8;
}

.calendar-day.p3 {
  background: #5ACC87;
}

.calendar-day.p1 .calendar-date,
.calendar-day.p2 .calendar-date,
.calendar-day.p3 .calendar-date {
  color: rgba(15, 90, 48, .65);
}

.calendar-day.today {
  box-shadow: inset 0 0 0 1.5px #F5920A;
}

.calendar-day.calendar-empty {
  background: transparent;
}

.calendar-day.future {
  background: transparent;
  border: 1px dashed #D3D0DE;
}

.calendar-day.future .calendar-date {
  color: #B3B3C0;
}

.calendar-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 14px;
}

.calendar-legend span {
  width: 14px;
  height: 14px;
  border-radius: 4px;
}

</style>
